import { inject, Injectable } from '@angular/core';
import { InterpolationParameters, TranslateService } from '@ngx-translate/core';
import { get } from 'lodash';
import { IApiError, IApiErrorDetail, IFormattedApiError } from '../interfaces/api-error.interface';
import { CaseConverter } from '../utils/case-converter';

@Injectable({
  providedIn: 'root',
})
export class ErrorFormatterService {
  public static readonly nonVisualErrorCode = 'ERR_1003' as const;

  private readonly translate = inject(TranslateService);

  public getErrorDetails(error: IApiError): IApiErrorDetail[] {
    if (!error?.details) {
      return [];
    }

    return Array.isArray(error.details) ? error.details : [error.details];
  }

  public getErrorMessage(detail?: IApiErrorDetail, error?: IApiError): IFormattedApiError {
    if (!detail && !error) {
      return { message: '', translationKey: '' };
    }

    if (detail) {
      return this.getErrorMessageFromDetail(detail, error);
    }

    return this.getErrorMessageFromError(error!);
  }

  private getErrorMessageFromDetail(detail: IApiErrorDetail, error?: IApiError): IFormattedApiError {
    const translationKey = this.getTranslationKey(detail, error);
    const message = this.translate.instant(
      translationKey,
      CaseConverter.deeplyConvertKeys(detail.params, CaseConverter.toCamelCase) as InterpolationParameters,
    );
    let unknownMessage: string | null = null;

    if (!message || !translationKey || message === translationKey) {
      unknownMessage = this.translate.instant('apiErrorMessages.unknown');
    }

    return {
      message: unknownMessage ?? message,
      translationKey,
    };
  }

  private getErrorMessageFromError(error: IApiError): IFormattedApiError {
    const details = Array.isArray(error.details) ? error.details : [error.details];

    return this.getErrorMessageFromDetail(details[0]!);
  }

  private getTranslationKey(detail: IApiErrorDetail, error?: IApiError): string {
    const translationKey = `apiErrorMessages.${CaseConverter.toCamelCase(detail.code)}${detail.params?.['entity'] ? `.${CaseConverter.toCamelCase(detail.params['entity'] as string)}` : detail.field ? `.${CaseConverter.toCamelCase(detail.field)}` : ''}`;
    const userGroupSwitchedAfterEndDateReadReached = get(error, 'debug_info.auto_switched_user_group', false);

    if (userGroupSwitchedAfterEndDateReadReached) {
      return `${translationKey.split('.').slice(0, -1).join('.')}.userGroupSwitched`;
    }

    return translationKey;
  }
}
